/**
 * Benefits analytics — usage, revenue and discount given over time, plus the
 * campaigns, institutions and partners doing the most work.
 *
 * Read-only. Every figure comes from `get_coupon_console_analytics`, which is
 * admin-gated in the database.
 */

import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Download, RefreshCw, TrendingUp } from "lucide-react";
import { downloadBlob, formatMoney, toCsv, type CouponRow } from "@/lib/couponConsole";

interface Analytics {
  totals?: { uses: number; attempts: number; revenue: number; discount: number; conversion_pct: number };
  daily?: Array<{ day: string; uses: number; revenue: number; discount: number }>;
  top_coupons?: Array<{ code: string; label: string; benefit_type: string; uses: number; attempts: number; revenue: number; discount: number }>;
  by_benefit_type?: Array<{ benefit_type: string; uses: number; revenue: number; discount: number }>;
  top_institutions?: Array<{ name: string; uses: number; revenue: number }>;
  top_partners?: Array<{ name: string; uses: number; revenue: number }>;
}

const RANGES = [
  { value: "7", label: "Last 7 days" },
  { value: "30", label: "Last 30 days" },
  { value: "90", label: "Last 90 days" },
  { value: "365", label: "Last year" },
];

const SLICE_COLORS = [
  "hsl(var(--primary))",
  "hsl(var(--accent))",
  "hsl(var(--secondary))",
  "hsl(var(--muted-foreground))",
  "hsl(var(--destructive))",
];

const EmptyChart = ({ label }: { label: string }) => (
  <div className="h-56 flex items-center justify-center text-sm text-muted-foreground text-center px-6">
    {label}
  </div>
);

const CouponAnalytics = ({ coupons }: { coupons: CouponRow[] }) => {
  const [days, setDays] = useState("90");
  const [data, setData] = useState<Analytics | null>(null);
  const [loading, setLoading] = useState(true);

  const load = async (range: string) => {
    setLoading(true);
    const { data: result, error } = await supabase.rpc("get_coupon_console_analytics", {
      p_days: Number(range),
    });
    if (error) {
      toast.error("Could not load analytics", { description: error.message });
      setData(null);
    } else {
      setData((result ?? {}) as Analytics);
    }
    setLoading(false);
  };

  useEffect(() => {
    void load(days);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);

  const currency = coupons[0]?.currency ?? "INR";
  const totals = data?.totals;
  const daily = data?.daily ?? [];
  const topCoupons = data?.top_coupons ?? [];
  const byType = data?.by_benefit_type ?? [];

  const expiringSoon = useMemo(() => {
    const soon = Date.now() + 7 * 24 * 60 * 60 * 1000;
    return coupons.filter(
      (c) =>
        c.status === "active" &&
        c.valid_until &&
        new Date(c.valid_until).getTime() <= soon &&
        new Date(c.valid_until).getTime() > Date.now(),
    );
  }, [coupons]);

  const nearlyExhausted = useMemo(
    () =>
      coupons.filter(
        (c) =>
          c.max_redemptions != null &&
          c.max_redemptions > 0 &&
          c.redemption_count / c.max_redemptions >= 0.8,
      ),
    [coupons],
  );

  const exportCsv = () => {
    const records = topCoupons.map((c) => ({
      code: c.code,
      campaign: c.label,
      type: c.benefit_type,
      attempts: c.attempts,
      uses: c.uses,
      revenue: c.revenue,
      discount_given: c.discount,
    }));
    if (records.length === 0) {
      toast.info("Nothing to export yet.");
      return;
    }
    downloadBlob(
      `benefits-analytics-${days}d.csv`,
      new Blob([toCsv(records)], { type: "text/csv;charset=utf-8" }),
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Select value={days} onValueChange={setDays}>
          <SelectTrigger className="w-44 min-h-11">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {RANGES.map((r) => (
              <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex gap-2">
          <Button variant="outline" className="min-h-11" onClick={() => void load(days)}>
            <RefreshCw className="w-4 h-4 mr-2" aria-hidden="true" />
            Refresh
          </Button>
          <Button variant="outline" className="min-h-11" onClick={exportCsv}>
            <Download className="w-4 h-4 mr-2" aria-hidden="true" />
            Export
          </Button>
        </div>
      </div>

      {/* Headline numbers */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[
          { label: "Redemptions", value: totals ? String(totals.uses) : "—" },
          { label: "Revenue", value: totals ? formatMoney(totals.revenue, currency) : "—" },
          { label: "Discount given", value: totals ? formatMoney(totals.discount, currency) : "—" },
          { label: "Conversion", value: totals ? `${totals.conversion_pct}%` : "—" },
        ].map((stat) => (
          <Card key={stat.label} className="rounded-3xl">
            <CardContent className="p-5">
              <p className="text-xs text-muted-foreground">{stat.label}</p>
              {loading ? (
                <Skeleton className="h-8 w-24 mt-2" />
              ) : (
                <p className="text-2xl font-bold mt-1">{stat.value}</p>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {(expiringSoon.length > 0 || nearlyExhausted.length > 0) && (
        <Card className="rounded-3xl border-primary/30 bg-primary/5">
          <CardContent className="p-5 space-y-2 text-sm">
            {expiringSoon.length > 0 && (
              <p>
                <strong>{expiringSoon.length}</strong> active campaign
                {expiringSoon.length === 1 ? "" : "s"} end within a week:{" "}
                <span className="font-mono">{expiringSoon.map((c) => c.code).join(", ")}</span>
              </p>
            )}
            {nearlyExhausted.length > 0 && (
              <p>
                <strong>{nearlyExhausted.length}</strong> campaign
                {nearlyExhausted.length === 1 ? " is" : "s are"} at 80% of their usage cap:{" "}
                <span className="font-mono">{nearlyExhausted.map((c) => c.code).join(", ")}</span>
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Trend */}
      <Card className="rounded-3xl">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-primary" aria-hidden="true" />
            Revenue and discount over time
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <Skeleton className="h-56 w-full" />
          ) : daily.length === 0 ? (
            <EmptyChart label="No coupon redemptions in this period yet." />
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <AreaChart data={daily}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="day" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                <Tooltip
                  contentStyle={{
                    background: "hsl(var(--card))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: 16,
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="revenue"
                  name="Revenue"
                  stroke="hsl(var(--primary))"
                  fill="hsl(var(--primary))"
                  fillOpacity={0.2}
                />
                <Area
                  type="monotone"
                  dataKey="discount"
                  name="Discount"
                  stroke="hsl(var(--accent))"
                  fill="hsl(var(--accent))"
                  fillOpacity={0.15}
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="rounded-3xl">
          <CardHeader>
            <CardTitle className="text-base">Top campaigns by revenue</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-56 w-full" />
            ) : topCoupons.length === 0 ? (
              <EmptyChart label="No campaign has been redeemed in this period." />
            ) : (
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={topCoupons.slice(0, 8)}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="code" tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
                  <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                  <Tooltip
                    contentStyle={{
                      background: "hsl(var(--card))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: 16,
                    }}
                  />
                  <Bar dataKey="revenue" name="Revenue" fill="hsl(var(--primary))" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card className="rounded-3xl">
          <CardHeader>
            <CardTitle className="text-base">Where the discount goes</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-56 w-full" />
            ) : byType.length === 0 ? (
              <EmptyChart label="No benefit type has been used in this period." />
            ) : (
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie
                    data={byType}
                    dataKey="discount"
                    nameKey="benefit_type"
                    innerRadius={50}
                    outerRadius={90}
                    paddingAngle={2}
                  >
                    {byType.map((entry, i) => (
                      <Cell key={entry.benefit_type} fill={SLICE_COLORS[i % SLICE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      background: "hsl(var(--card))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: 16,
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {[
          { title: "Top institutions", rows: data?.top_institutions ?? [] },
          { title: "Top partners", rows: data?.top_partners ?? [] },
        ].map((block) => (
          <Card key={block.title} className="rounded-3xl">
            <CardHeader>
              <CardTitle className="text-base">{block.title}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {loading ? (
                <Skeleton className="h-24 w-full" />
              ) : block.rows.length === 0 ? (
                <p className="text-sm text-muted-foreground">Nothing recorded yet.</p>
              ) : (
                block.rows.map((r) => (
                  <div key={r.name} className="flex items-center justify-between text-sm py-1.5">
                    <span className="truncate pr-3">{r.name}</span>
                    <span className="text-muted-foreground shrink-0">
                      {r.uses} · {formatMoney(r.revenue, currency)}
                    </span>
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default CouponAnalytics;
